// ============================================================================
// Pure mapping helpers for the unified Contact + Enquiry form.
//
// No React, no side effects, nothing async — these just turn form state into
// the two API payloads (buildContactPayload / buildEnquiryPayload) and turn
// API responses back into form state (contactToFormPatch / enquiryToFormPatch).
// The backend can't change, so both API calls and their field names below
// are unchanged from before — only the *shape of the form state feeding them*
// has been simplified.
// ============================================================================

export const parseLatLngFromGoogleMapsUrl = url => {
  if (!url) return null;
  const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (atMatch) return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };

  const qMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (qMatch) return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };

  const llMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (llMatch) return { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]) };

  return null;
};

export const buildPipeSeparatedString = valuesObj =>
  Object.keys(valuesObj || {})
    .filter(key => valuesObj[key])
    .join('|');

export const parsePipeSeparatedString = (str, knownKeys) => {
  const active = new Set((str || '').split('|').filter(Boolean));
  return knownKeys.reduce((acc, key) => ({ ...acc, [key]: active.has(key) }), {});
};

const fullName = state => `${state.firstName || ''} ${state.lastName || ''}`.trim();

// ✅ Resolve the contact type to send: if 'Other' was chosen, send the custom text
const resolveContactType = state =>
  state.contactType === 'Other'
    ? (state.contactTypeOther || 'Other')
    : (state.contactType || '');

// Fields the Contact API expects but this form doesn't (yet) collect.
// Only sent on creation, blank — and never touched again once a contact
// exists, since edits below spread over the real record first.
const CONTACT_DEAD_FIELDS_ON_CREATE = {
  crmContactTitle: '',
  crmContactMiddle: '',
  crmTitle: '',
  crmIndustry: '',
  crmAddressCity: '',
  crmContactStatus: '',
  crmRating: '',
  crmProjectDescription: '',
  crmBudget: null,
  crmDeliverable: null,
  crmDoNotContact: null,
  crmDoNotEmail: null,
};

const effectiveSiteAddress = state =>
  state.sameAsHome
    ? {
      line1: state.homeAddressLine1,
      line2: state.homeAddressLine2,
      town: state.homeTown,
      zip: state.homeZip,
      country: state.homeCountry,
    }
    : {
      line1: state.siteAddressLine1,
      line2: state.siteAddressLine2,
      town: state.siteTown,
      zip: state.siteZip,
      country: state.siteCountry,
    };

export const buildContactPayload = ({ state, isNew, baseContact, teamId, stage }) => {
  const site = effectiveSiteAddress(state);

  const editableFields = {
    crmContactFirst: state.firstName || '',
    crmContactLast: state.lastName || '',
    crmContactPhone: state.phone || '',
    crmContactEmail: state.email || '',
    crmCompany: state.companyName || '',
    // ✅ contactType now maps to crmProjectType, with 'Other' resolving to custom text
    crmProjectType: resolveContactType(state),
    crmAddressLine1: site.line1 || '',
    crmAddressLine2: site.line2 || '',
    crmAddressTown: site.town || '',
    crmAddressZip: site.zip || '',
    crmAddressCountry: site.country || '',
    fellingLicenseNo: state.hasFellingLicense ? state.fellingLicenseNumber || '' : '',
    dropPin: state.dropPinUrl || '',
    dropPinLatitude: state.dropPinLat || 0,
    dropPinLongitude: state.dropPinLng || 0,
    crmTeamId: teamId,
    crmAddressCounty: state.crmAddressCounty || '',
    leadQuality: state.enquiryRating || '',
    crmLeadReferralSource: state.enquiryOriginatedFrom || '',
    // ✅ REMOVED: crmContactWebsite, crmLinkedInProfile
    crmBackgroundInfo: state.crmBackgroundInfo || '',
    followUpDate: state.followUpDate || null,
  };

  if (isNew) {
    return {
      crmStageName: stage?.label || '',
      crmStageId: stage?.StageId ?? 1,
      crmSalesRep: 1,
      ...CONTACT_DEAD_FIELDS_ON_CREATE,
      ...editableFields,
    };
  }

  // Editing an existing contact: start from the full fetched record so
  // fields this form never touches (rating, budget, do-not-contact, etc.)
  // survive untouched, then layer in what the user changed here.
  return {
    ...(baseContact || {}),
    ...editableFields,
    crmId: baseContact?.crmId,
  };
};

export const buildEnquiryPayload = ({ state, crmId, todaysDate, jobTypeKeys, speciesKeys }) => ({
  crmId,
  submissionDate: todaysDate,
  enquirerName: fullName(state),
  phoneNumber: state.phone,
  forestersName: state.foresterName,
  worrellRepDetails: state.completedBy,
  enquiryOriginatedFrom: state.enquiryOriginatedFrom,
  // ✅ transactionType renamed to enquiryType on the form — still sent as enquiryType
  enquiryType: state.enquiryType,
  ...(state.enquiryType === 'Timber Sale' && {
    customerName: state.salesCustomerName,
    forestManager: state.salesForestManager,
    sP_WHNumber: state.salesWhNumber,
    customerEmail: state.salesCustomerEmail,
    emailCopyToCustomer: state.salesEmailCopyToCustomer,
    wtgemailConfimPricing: state.salesWtgEmail,
    effectiveFrom: state.salesEffectiveFrom || null,
    effectiveTo: state.salesEffectiveTo || null,
    termsOfConditionsCustomer: state.salesTerms,
  }),
  siteEntranceLatitude: state.dropPinLat || 0,
  siteEntranceLongitude: state.dropPinLng || 0,
  haveFellingLicense: state.hasFellingLicense ? 'Yes' : 'No',
  fellingLicenseNumber: state.hasFellingLicense ? state.fellingLicenseNumber : '',
  fellingLicenseRef: state.hasFellingLicense ? state.fellingLicenseNumber : '',
  jobType: buildPipeSeparatedString(state.jobType),
  speciesType: buildPipeSeparatedString(state.species),
  forestDescription: state.forestDescription,
  sizeInHectares: state.sizeInHectares || null,
  managedBy: state.isForesterManaging ? 'Yes' : 'No',
  companyName: state.companyName,
  forestAddress: state.siteAddressLine1,
  forestCounty: state.siteTown,
  stateProvince: '',
  forestEircode: state.siteZip,
  country: state.siteCountry || 'Ireland',
  additionalDetails: state.additionalInfoFromGrower,
  followupDetails: state.followUpDetails,
  additionalNoteWithBrochure: state.brochureNote,
  followupDate: state.followUpDate || null,
  // ✅ REMOVED: allocatedTo, pipelineStatus
  enquiryRating: state.enquiryRating,
  title: state.oldEnquirerTitle,
  fullName: state.oldEnquirerFullName,
  show: state.typeOfEnquiry,
  forestryLicenseViewer: state.forestryLicenceViewer,
  landDirectViewer: state.landDirectViewer,
  sendBrochure: state.sendBrochure ? 'Yes' : 'No',
  dateSent: todaysDate,
  recordModifiedDate: null,
  homeAddress: state.homeAddressLine1,
  homeEirCode: state.homeZip,
  email: state.email,
  dropPin: state.dropPinUrl,
  leadQuality: state.enquiryRating,
});

// ── Reverse mappers: API record -> form state patch ────────────────────────

export const contactToFormPatch = contact => {
  console.log("CONTACT ===", contact)
  if (!contact) return {};

  const knownContactTypes = ['Private', 'Forester Company', 'Other'];
  const rawContactType = contact.crmProjectType || '';
  const isKnownType = knownContactTypes.includes(rawContactType);

  return {
    firstName: contact.crmContactFirst || '',
    lastName: contact.crmContactLast || '',
    phone: contact.crmContactPhone || '',
    email: contact.crmContactEmail || '',
    companyName: contact.crmCompany || '',
    // ✅ Rehydrate contactType + contactTypeOther
    contactType: isKnownType ? rawContactType : (rawContactType ? 'Other' : ''),
    contactTypeOther: isKnownType ? '' : rawContactType,
    crmAddressLine1: contact.crmAddressLine1 || '',
    homeAddressLine1: contact.crmAddressLine1 || '',
    homeAddressLine2: contact.crmAddressLine2 || '',
    siteTown: contact.crmAddressCounty || '',
    homeZip: contact.crmAddressZip || '',
    siteZip: contact.crmAddressZip || '',
    crmAddressCounty: contact.crmAddressCounty || '',
    homeCountry: contact.crmAddressCountry || 'Ireland',
    crmAddressCountry: contact.crmAddressCountry || 'Ireland',
    hasFellingLicense: Boolean(contact.fellingLicenseNo),
    fellingLicenseNumber: contact.fellingLicenseNo || '',
    dropPinUrl: contact.dropPin || '',
    dropPinLat: contact.dropPinLatitude || null,
    dropPinLng: contact.dropPinLongitude || null,
    sameAsHome: false,
    crmTeamId: contact.crmTeamId,
    crmStageId: contact.crmStageId,
    crmStageName: contact.crmStageName || '',
    crmSalesRep: contact.crmSalesRep,
    // ✅ REMOVED: crmLinkedInProfile, crmContactWebsite
    crmBackgroundInfo: contact.crmBackgroundInfo || '',
  };
};

// Applied *after* contactToFormPatch — the enquiry record is the more
// current source for anything enquiry-specific (site details included),
// so its values are allowed to overwrite what the contact patch set.
export const enquiryToFormPatch = (enquiry, jobTypeKeys, speciesKeys) => {
  if (!enquiry) return {};
  return {
    typeOfEnquiry: enquiry.show || 'Enquiry',
    // ✅ Removed `transactionType` — the form now only has `enquiryType`
    enquiryType: enquiry.enquiryType || enquiry.enquiryView || '',
    salesCustomerName: enquiry.customerName ?? '',
    salesForestManager: enquiry.forestManager ?? '',
    salesWhNumber: enquiry.sP_WHNumber ?? '',
    salesCustomerEmail: enquiry.customerEmail ?? '',
    salesEmailCopyToCustomer: enquiry.emailCopyToCustomer ?? 'No',
    salesWtgEmail: enquiry.wtgemailConfimPricing ?? '',
    salesEffectiveFrom: enquiry.effectiveFrom ? String(enquiry.effectiveFrom).substring(0, 10) : '',
    salesEffectiveTo: enquiry.effectiveTo ? String(enquiry.effectiveTo).substring(0, 10) : '',
    salesTerms: enquiry.termsOfConditionsCustomer ?? '',
    homeAddressLine1: enquiry.homeAddress || '',
    homeZip: enquiry.homeEirCode || '',
    foresterName: enquiry.forestersName || '',
    enquiryOriginatedFrom: enquiry.enquiryOriginatedFrom || '',
    isForesterManaging: enquiry.managedBy === 'Yes',
    sizeInHectares: enquiry.sizeInHectares || '',
    forestDescription: enquiry.forestDescription || '',
    siteAddressLine1: enquiry.forestAddress || '',
    ...(enquiry.forestCounty ? { siteTown: enquiry.forestCounty } : {}),
    siteZip: enquiry.forestEircode || '',
    siteCountry: enquiry.country || 'Ireland',
    jobType: parsePipeSeparatedString(enquiry.jobType, jobTypeKeys),
    species: parsePipeSeparatedString(enquiry.speciesType, speciesKeys),
    followUpDetails: enquiry.followupDetails || '',
    followUpDate: enquiry.followupDate ? enquiry.followupDate.substring(0, 10) : '',
    // ✅ REMOVED: allocatedTo, pipelineStatus
    enquiryRating: enquiry.enquiryRating || '',
    additionalInfoFromGrower: enquiry.additionalDetails || '',
    forestryLicenceViewer: enquiry.forestryLicenseViewer || '',
    landDirectViewer: enquiry.landDirectViewer || '',
    oldEnquirerTitle: enquiry.title || '',
    oldEnquirerFullName: enquiry.fullName || '',
    sendBrochure: enquiry.sendBrochure === 'Yes',
    brochureNote: enquiry.additionalNoteWithBrochure || '',
  };
};